import { serializers } from "@imminently/react/hooks/use-local-storage";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-pro";
import type { GridApiPro } from "@mui/x-data-grid-pro/models/gridApiPro";
import debounce from "lodash/debounce";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  memo,
  FC,
  PropsWithChildren,
  useEffect,
  MutableRefObject,
} from "react";

import {
  COLUMN_NAMES,
  patientListColumns,
} from "@/components/PatientList/PatientListColumns";
import {
  useLocalStorage,
  Options,
} from "@/pages/home/patient-list/_TEMP_useLocalStorage";

type ColumnName = (typeof COLUMN_NAMES)[number];
type ColumnWidths = { [K in ColumnName]: number };
type ColumnVisibilities = { [K in ColumnName]: boolean };
export type PinnedColumnsState = { left?: string[]; right?: string[] };

type LocalStorageValue = {
  columnsVisibility: ColumnVisibilities;
  orderedColNames: ColumnName[];
  colWidths: ColumnWidths;
  pinnedColumns: PinnedColumnsState;
};

const defaultWidths = COLUMN_NAMES.reduce(
  (widths, colName) => ({ ...widths, [colName]: 100 }),
  {}
) as ColumnWidths;

const colDefsToColWidths = (colDefs: GridColDef<any, any, any>[]): ColumnWidths => (
  colDefs.reduce<ColumnWidths>((a, { field, width }) => {
    const typedField = field as keyof typeof a;
    if (a[typedField] === undefined) return a;

    a[typedField] = width || a[typedField];
    return a;
  }, defaultWidths)
);

const defaultVisibility = COLUMN_NAMES.reduce(
  (visibility, colName) => ({ ...visibility, [colName]: true }),
  {}
) as ColumnVisibilities;

const defaultOrder = patientListColumns
  .filter(it => COLUMN_NAMES.includes(it.field as ColumnName))
  .map((it) => it.field) as ColumnName[];

export const defaultPinnedColumns: PinnedColumnsState = { left: ["patientName", "sex"] };

const defaultLocalStorageValue: LocalStorageValue = {
  columnsVisibility: defaultVisibility,
  orderedColNames: defaultOrder,
  colWidths: colDefsToColWidths(patientListColumns),
  pinnedColumns: defaultPinnedColumns,
};

const localStorageOptions: Options<LocalStorageValue> = {
  ...serializers.json,
  deserialize: (value) => {
    const fallback = defaultLocalStorageValue;
    try {
      const parsed: LocalStorageValue = JSON.parse(value);

      switch (true) {
        case parsed.colWidths === undefined:
        case typeof parsed.colWidths !== "object":
        case parsed.colWidths === null:

        // eslint-disable-next-line no-fallthrough
        case parsed.columnsVisibility === undefined:
        case typeof parsed.columnsVisibility !== "object":
        case parsed.columnsVisibility === null:

        // eslint-disable-next-line no-fallthrough
        case parsed.orderedColNames === undefined:
        case Array.isArray(parsed.orderedColNames) === false:
          return fallback;

        default: {
          let pinnedColumns = fallback.pinnedColumns;
          if (parsed.pinnedColumns && typeof parsed.pinnedColumns === "object") {
            pinnedColumns = getValidPinnedColumns(parsed.pinnedColumns);
          } else {
            // Migrate from legacy trierptntlst storage if present
            try {
              const legacy = localStorage.getItem("trierptntlst");
              if (legacy) {
                const legacyParsed = JSON.parse(legacy);
                const legacyPinned = legacyParsed?.pinnedColumns;
                if (legacyPinned && typeof legacyPinned === "object") {
                  pinnedColumns = getValidPinnedColumns(legacyPinned);
                }
              }
            } catch {
              /* ignore */
            }
          }
          return { ...parsed, pinnedColumns };
        }
      }
    } catch (e) {
      console.warn("B7oo2Akjm4 | json parse failed", e);
      return fallback;
    }
  },
  key: "trier.useLocalStorage.columns-metadata",
  defaultValue: defaultLocalStorageValue,
};

type ApiRef = GridApiPro;
type PatientListApiRefEmptyState = { type: "empty" };

type PatientListApiRefHydratedState = {
  type: "hydrated";
  apiRef: ApiRef;
};

type PatientListApiRefState =
  | PatientListApiRefEmptyState
  | PatientListApiRefHydratedState;

const columnFields = new Set(COLUMN_NAMES as readonly string[]);

const getValidPinnedColumns = (saved: PinnedColumnsState): PinnedColumnsState => {
  const result: PinnedColumnsState = {};
  if (Array.isArray(saved.left)) {
    result.left = saved.left.filter((f) => typeof f === "string" && columnFields.has(f));
  }
  if (Array.isArray(saved.right)) {
    result.right = saved.right.filter((f) => typeof f === "string" && columnFields.has(f));
  }
  if ((result.left?.length ?? 0) === 0 && (result.right?.length ?? 0) === 0) {
    return defaultPinnedColumns;
  }
  return result;
};

export type PatientListApiRefCtxValue = {
  state:
  | PatientListApiRefEmptyState
  | (PatientListApiRefHydratedState & {
    localStorageValue: LocalStorageValue;
  });
  apiRef: MutableRefObject<GridApiPro>;
  setRef: (ref: ApiRef) => unknown;
  toggleColumnVisibility: (name: ColumnName) => unknown;
  persistOrderedColNames: () => unknown;
  persistColWidths: () => unknown;
  persistPinnedColumns: (pinned: PinnedColumnsState) => void;
};

export const defaultPatientListApiRefCtxValue: PatientListApiRefCtxValue = {
  state: { type: "empty" },
  apiRef: undefined as any,
  setRef: console.log,
  toggleColumnVisibility: console.log,
  persistOrderedColNames: console.log,
  persistColWidths: console.log,
  persistPinnedColumns: () => {},
};

export const patientListApiRefCtx = createContext(
  defaultPatientListApiRefCtxValue
);

export const usePatientListApiRefCtx = () => useContext(patientListApiRefCtx);

export const WithPatientListApiRefCtx: FC<PropsWithChildren> = memo((p) => {
  const [value, setValue] = useState<PatientListApiRefState>(
    defaultPatientListApiRefCtxValue.state
  );
  const apiRef = useGridApiRef();
  const valueRef = useRef(value);
  if (valueRef.current !== value) valueRef.current = value;

  const {
    ref: localStorageValueRef,
    value: localStorageValue,
    setValue: setLStorageValue,
  } = useLocalStorage(localStorageOptions);

  const setRef = useCallback<PatientListApiRefCtxValue["setRef"]>(
    (ref) => {
      if (!ref) return;

      setValue({ type: "hydrated", apiRef: ref });

      ref.setColumnVisibilityModel(
        localStorageValueRef.current.columnsVisibility
      );

      localStorageValueRef.current.orderedColNames.forEach((name, i) => {
        ref.setColumnIndex(name, i);
      });

      Object.entries(localStorageValueRef.current.colWidths).forEach(
        ([name, width]) => {
          ref.setColumnWidth(name, width);
        }
      );

      const pinned = getValidPinnedColumns(localStorageValueRef.current.pinnedColumns);
      ref.setPinnedColumns(pinned);
    },
    [setValue]
  );

  useEffect(() => {
    console.log("api ref changed", apiRef.current);
    setRef(apiRef.current);
  }, [apiRef.current]);

  const toggleColumnVisibility = useCallback<
    PatientListApiRefCtxValue["toggleColumnVisibility"]
  >(
    (name) => {
      const { current: valueCurrent } = valueRef;
      if (valueCurrent.type === "empty") return;

      const { columnsVisibility } = localStorageValueRef.current;
      const nextLStorageValue: typeof localStorageValueRef.current = {
        ...localStorageValueRef.current,
        columnsVisibility: {
          ...columnsVisibility,
          [name]: !columnsVisibility[name],
        },
      };

      valueCurrent.apiRef.setColumnVisibilityModel(
        nextLStorageValue.columnsVisibility
      );

      setLStorageValue(nextLStorageValue);
    },
    [setLStorageValue]
  );

  const persistOrderedColNames = useCallback<
    PatientListApiRefCtxValue["persistOrderedColNames"]
  >(() => {
    const { current: valueCurrent } = valueRef;
    if (valueCurrent.type === "empty") return;

    setLStorageValue((prev) => ({
      ...prev,
      orderedColNames: valueCurrent.apiRef.state.columns
        .orderedFields as ColumnName[],
    }));
  }, [setLStorageValue]);

  const persistColWidths = useMemo<
    PatientListApiRefCtxValue["persistColWidths"]
  >(
    () =>
      debounce(() => {
        const { current: valueCurrent } = valueRef;
        if (valueCurrent.type === "empty") return;

        const nextColWidths = colDefsToColWidths(
          Object.values(valueCurrent.apiRef.state.columns.lookup)
        );

        setLStorageValue((prev) => ({
          ...prev,
          colWidths: nextColWidths,
        }));
      }, 1_000),
    [setLStorageValue]
  );

  const persistPinnedColumns = useCallback<
    PatientListApiRefCtxValue["persistPinnedColumns"]
  >(
    (pinned) => {
      setLStorageValue((prev) => ({
        ...prev,
        pinnedColumns: getValidPinnedColumns(pinned),
      }));
    },
    [setLStorageValue]
  );

  const ctxValue = useMemo<PatientListApiRefCtxValue>(
    () => ({
      state:
        value.type === "empty"
          ? value
          : { ...value, localStorageValue: localStorageValue },
      apiRef,
      setRef,
      toggleColumnVisibility,
      persistOrderedColNames,
      persistColWidths,
      persistPinnedColumns,
    }),
    [
      value,
      localStorageValue,
      apiRef,
      setRef,
      toggleColumnVisibility,
      persistOrderedColNames,
      persistColWidths,
      persistPinnedColumns,
    ]
  );

  return (
    <patientListApiRefCtx.Provider value={ctxValue}>
      {p.children}
    </patientListApiRefCtx.Provider>
  );
});

WithPatientListApiRefCtx.displayName = "patientList/WithPatientListApiRefCtx";
